<template>
  <div class="front-wrapper min-vh-100 p-0 patient-change-regimen-new">
    <header class="main-header">
      <div class="container container-md-lg">
        <div class="header-wrap wrap-lg">
          <div class="header-logo">
            <router-link
              :to="{
                name: 'userdashboard'
              }"
            >
              <img
                :src="logo"
                alt="Logo"
              >
            </router-link>
          </div>
          <div class="header-right">
            <div class="header-center">
              <h4>{{ regimen.id ? 'Update' : 'Create' }} Preset Regimen</h4>
            </div>
            <p class="text-soft pt-1">
              Add and remove products from your preset regimen
            </p>
          </div>
          <div class="header-right-link">
            <a
              href="#"
              @click.prevent="leaveWithoutSaving"
            >Back</a>
          </div>
        </div>
      </div>
    </header>

    <div class="frontend-main p-0 m-0">
      <div class="container container-md-xs">
        <Notifications />
      </div>
      <div class="container-fluid p-0">
        <!-- Two Panel Layout -->
        <div class="partition">
          <!-- Left Panel: Add Product -->
          <div class="partition-left">
            <div class="section-top">
              <h3 class="panel-title">Add product</h3>

              <!-- Search Bar -->
              <div class="search-bar">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17Z" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M19 19L14.65 14.65" stroke="#9CA3AF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <input
                  v-model="search"
                  type="text"
                  placeholder="Search product by name"
                  @input="searchProduct"
                >
              </div>

              <!-- Filters -->
              <div class="filters-container">
                <h4 class="filters-title">Filters</h4>
                <div class="row">  
                  <div class="col-sm-3 custom-form form-filter">
                      <div class="form1-items">
                        <label>
                          <input
                            v-model="productsType"
                            type="radio"
                            value=""
                            name="productsType"
                            @change="fetchData"
                          >
                          <span class="type-heading">All Products</span>
                        </label>
                      </div>

                      <div v-if="physicianId" class="form1-items">
                        <label>
                          <input
                            v-model="productsType"
                            type="radio"
                            value="topPicks"
                            name="productsType"
                            @change="fetchData"
                          >
                          <span class="type-heading">Top Picks</span>
                        </label>  
                      </div>
                      <div v-else class="form1-items">
                        <label>
                          <input
                            v-model="productsType"
                            type="radio"
                            value="allPhysiciantopPicks"
                            name="productsType"
                          >
                          <span class="type-heading">Top Picks</span>
                        </label>
                      </div>
                    </div>
                  <div class="col-sm-9">  
                    <div class="filters-grid">
                      <filter-select v-if="brands" v-model="brand" :items="brands" default-value="All Brands" name="brands-select" class="filter-dropdown" />
                      <filter-select v-if="concerns" v-model="concern" :items="concerns" default-value="All Concerns" name="concerns-select" class="filter-dropdown" />
                      <filter-select v-if="categories" v-model="category" :items="categories" default-value="All Types" name="categories-select" class="filter-dropdown" />
                      <filter-select v-if="ingredients" v-model="ingredient" :items="ingredients" default-value="All Ingredients" name="ingredients-select" class="filter-dropdown" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Search Status and Active Filters -->
              <div v-if="isSearching || searchCompleted || (activeFilters && activeFilters.length > 0)" class="search-status-section">
                <!-- Search Indicator -->
                <div v-if="isSearching" class="search-indicator searching">
                  <svg class="spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" stroke-opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
                  </svg>
                  <span>Searching...</span>
                </div>
                <div v-else-if="searchCompleted" class="search-indicator completed">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17L4 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Search complete</span>
                </div>

                <!-- Active Filter Tags -->
                <div v-if="activeFilters && activeFilters.length > 0" class="active-filters">
                  <div class="filter-tags">
                    <button 
                      v-for="filter in activeFilters" 
                      :key="filter.type"
                      class="filter-tag"
                      @click="removeFilter(filter.type)"
                      :title="`Remove ${filter.type} filter`"
                    >
                      <span class="filter-tag-label">{{ filter.label }}</span>
                      <svg class="filter-tag-close" width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                      </svg>
                    </button>
                    <button class="clear-all-filters" @click="clearAllFilters">
                      Clear all
                    </button>
                  </div>
                </div>
              </div>
            </div>


            <!-- Products Section -->
            <TopPicksNew
              v-if="productsType === 'allPhysiciantopPicks'"
              class="products-section"
              :query="query"
              :sort-key="sortKey"
              :reverse="reverse"
              :practice="regimen.practice"
              :morning-products="morningProducts"
              :evening-products="eveningProducts"
              @open-preset-product="handlePresetProduct"
            />
            <div v-else class="products-section">
              <h4 class="section-title">Products</h4>

              <div v-if="sending" class="loading-state">Loading products...</div>
              <div v-else-if="!products || !products.length" class="no-products">No products found</div>

              <div v-else-if="products && products.length" class="products-list">
                <div
                  v-for="product in filteredProducts"
                  :key="product.id"
                  class="product-wrapper"
                  draggable="true"
                  @dragstart="startDragProduct($event, product)"
                >
                  <div class="product-item">

                    <!-- Image + stacked name / vendor -->
                    <div class="product-info-wrapper">
                      <img
                        :src="(product.images && product.images.edges && product.images.edges[0] && product.images.edges[0].node && product.images.edges[0].node.url) || (product.images && product.images[0] && product.images[0].url) || '/images/product-placeholder.jpg'"
                        :alt="product.title"
                        class="product-image"
                      >
                      <div class="product-details">
                        <p class="product-name">{{ product.title }}</p>
                        <p class="product-vendor">{{ product.vendor }}</p>
                      </div>
                    </div>

                    <!-- View Details + time buttons -->
                    <div class="product-actions">
                      <a href="#" class="btn-view" @click.prevent="toggleProduct(product.id)">
                        {{ expandedProduct === product.id ? 'Close Details' : 'View Details' }}
                      </a>

                      <div class="time-actions">
                        <!-- AM -->
                        <button 
                          class="time-btn" 
                          title="Morning" 
                          @click="addProductToRegimen(product, 'am')"
                          :disabled="isProductInMorning(product)"
                          :class="{ 'disabled': isProductInMorning(product) }"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="4"/>
                            <line x1="12" y1="2"  x2="12" y2="4"/>
                            <line x1="12" y1="20" x2="12" y2="22"/>
                            <line x1="4.22" y1="4.22"   x2="5.64"  y2="5.64"/>
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                            <line x1="2"  y1="12" x2="4"  y2="12"/>
                            <line x1="20" y1="12" x2="22" y2="12"/>
                            <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36"/>
                            <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"/>
                          </svg>
                        </button>
                        <!-- PM -->
                        <button 
                          class="time-btn" 
                          title="Evening" 
                          @click="addProductToRegimen(product, 'pm')"
                          :disabled="isProductInEvening(product)"
                          :class="{ 'disabled': isProductInEvening(product) }"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                          </svg>
                        </button>
                        <!-- AM + PM -->
                        <button 
                          class="time-btn time-btn--both" 
                          title="Both AM & PM" 
                          @click="addProductToRegimen(product, 'both')"
                          :disabled="isProductInMorning(product) || isProductInEvening(product)"
                          :class="{ 'disabled': isProductInMorning(product) || isProductInEvening(product) }"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="4"/>
                            <line x1="12" y1="2"  x2="12" y2="4"/>
                            <line x1="12" y1="20" x2="12" y2="22"/>
                            <line x1="4.22" y1="4.22"   x2="5.64"  y2="5.64"/>
                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                            <line x1="2"  y1="12" x2="4"  y2="12"/>
                            <line x1="20" y1="12" x2="22" y2="12"/>
                            <line x1="4.22"  y1="19.78" x2="5.64"  y2="18.36"/>
                            <line x1="18.36" y1="5.64"  x2="19.78" y2="4.22"/>
                          </svg>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Expandable Description -->
                  <div v-if="expandedProduct === product.id" class="product-description">
                    <div v-if="product.descriptionHtml" class="description-content">
                      <h5>Description:</h5>
                      <div class="description-text" v-html="product.descriptionHtml" />
                    </div>
                    <div v-else class="description-content">
                      <p class="no-description">No description available for this product.</p>
                    </div>
                  </div>
                </div>
                 <!-- Pagination -->
              <div class="pagination-wrap">
                <div class="pagination-l">
                  <div class="page-results">
                    Display
                    <select v-model="productsPerPage" @change="changePerPage">
                      <option value="20">20</option>
                      <option value="30">30</option>
                      <option value="40">40</option>
                      <option value="50">50</option>
                    </select>
                    results per page
                  </div>
                </div>
                <div class="pagination-r">
                  <nav class="pagination">
                    <ul class="pagination">
                      <li>
                        <a href="#" :class="{ disabled: !hasPreviousPage }" @click.prevent="hasPreviousPage && showPrevProducts()">
                          <i class="fas fa-angle-left" />
                        </a>
                      </li>
                      <li>
                        <a href="#" :class="{ disabled: !hasNextPage }" @click.prevent="hasNextPage && getMoreProducts()">
                          <i class="fas fa-angle-right" />
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
              </div>
            </div>
          </div>

          <!-- Right Panel: Regimen Name -->
          <div 
            class="partition-right"
            @dragleave="onDragLeavePartition($event)"
            @dragenter.prevent="onDragEnterPartition($event)"
            @drop="onDropInPartition($event)"
          >

            <div class="regimen-header">
              <h1 class="regimen-title">Clients Regimen</h1>
              <p class="regimen-subtitle">Drag and drop to rearrange products, drag off Regimen to remove entirely.</p>
            </div>

            <div 
              v-for="pageIndex in totalPages" 
              :key="'page-' + pageIndex"
              class="regimen-preview-block"
            >
              <div class="regimen-brand p-2 px-4">
                <span class="brand-name">
                  <img 
                    src="../../../../images/preview-logo.png" 
                    alt="RegimenPro Logo"
                    :class="{'no-border': !practiceLogo}"
                  >
                  <img
                    v-if="practiceLogo"
                    :src="practiceLogo"
                    alt="Practice Logo"
                  >
                </span>
                <div class="client-details">
                  <p class="m-0">
                    <strong> Preset Regimen : </strong> {{ (regimen && regimen.name) || 'New Regimen' }}
                  </p>
                  <p class="m-0" v-if="physicianName">
                    <strong> Provider : </strong> {{ physicianName }}
                  </p>
                </div>
              </div>

              <!-- Morning + Evening columns -->
              <div class="regimen-columns">

                <!-- Morning -->
                <div class="regimen-col-morning">
                  <div class="regimen-col-title">Morning Regimen</div>

                  <draggable 
                    :list="getMorningProductsForPage(pageIndex - 1)" 
                    :options="dragOptions"
                    @change="handlePaginatedDragChange($event, 'morning', pageIndex - 1)"
                    @start="onDragStart($event, 'morning', pageIndex - 1)"
                    @end="onDragEnd($event)"
                    class="draggable-container"
                  >
                    <div 
                      v-for="(product, index) in getMorningProductsForPage(pageIndex - 1)" 
                      :key="product.variant_graph_id || index"
                      class="drop-zone-wrapper"
                      :class="{ 'dragging-to-remove': isDraggingOutside && draggedProduct === product }"
                    >
                      <div class="drop-zone with-product d-flex justify-content-between">
                        <span class="product-number">{{ (pageIndex - 1) * 8 + index + 1 }}</span>
                        <div class="product-name d-flex flex-column text-left">
                          <p>{{ product.title }}</p>
                          <span>{{ product.vendor }}</span>
                          <a href="#" @click.prevent="removeProduct(product, 'morning')">Remove</a>
                        </div>
                        <img :src="product.image" alt="product thumbnail" class="product-thumbnail">
                      </div>
                      <div v-if="index < getMorningProductsForPage(pageIndex - 1).length - 1 || isLastMorningPage(pageIndex - 1)" class="arrow-down"><img src="../../../../images/pdf-down-arrow.png" alt="Down arrow"></div>
                    </div>
                  </draggable>

                  <template v-if="isLastMorningPage(pageIndex - 1)">
                    <div 
                      v-for="n in Math.max(1, 3 - getMorningProductsForPage(pageIndex - 1).length)"
                      :key="'morning-empty-' + n"
                      class="drop-zone-wrapper"
                    >
                      <div 
                        class="drop-zone"
                        @drop="onDropMorning($event)"
                        @dragover.prevent
                      @dragenter.prevent
                    >
                      + Drag product here to add
                    </div>
                    <div v-if="n < Math.max(1, 3 - getMorningProductsForPage(pageIndex - 1).length)" class="arrow-down"><img src="../../../../images/pdf-down-arrow.png" alt="Down arrow"></div>
                  </div>
                  </template>

                  <!-- Additional info removed -->
                </div>

                <!-- Evening -->
                <div class="regimen-col-evening">
                  <div class="regimen-col-title">Evening Regimen</div>

                  <draggable 
                    :list="getEveningProductsForPage(pageIndex - 1)" 
                    :options="dragOptions"
                    @change="handlePaginatedDragChange($event, 'evening', pageIndex - 1)"
                    @start="onDragStart($event, 'evening', pageIndex - 1)"
                    @end="onDragEnd($event)"
                    class="draggable-container"
                  >
                    <div 
                      v-for="(product, index) in getEveningProductsForPage(pageIndex - 1)" 
                      :key="product.variant_graph_id || index"
                      class="drop-zone-wrapper"
                      :class="{ 'dragging-to-remove': isDraggingOutside && draggedProduct === product }"
                    >
                      <div class="drop-zone with-product d-flex justify-content-between">
                        <span class="product-number">{{ (pageIndex - 1) * 8 + index + 1 }}</span>
                        <div class="product-name d-flex flex-column text-left">
                          <p>{{ product.title }}</p>
                          <span>{{ product.vendor }}</span>
                          <a href="#" @click.prevent="removeProduct(product, 'evening')">Remove</a>
                        </div>
                        <img :src="product.image" alt="product thumbnail" class="product-thumbnail">
                      </div>
                      <div v-if="index < getEveningProductsForPage(pageIndex - 1).length - 1 || isLastEveningPage(pageIndex - 1)" class="arrow-down"><img src="../../../../images/pdf-down-arrow.png" alt="Down arrow"></div>
                    </div>
                  </draggable>

                  <template v-if="isLastEveningPage(pageIndex - 1)">
                    <div 
                      v-for="n in Math.max(1, 3 - getEveningProductsForPage(pageIndex - 1).length)"
                      :key="'evening-empty-' + n"
                      class="drop-zone-wrapper"
                    >
                      <div 
                        class="drop-zone"
                        @drop="onDropEvening($event)"
                        @dragover.prevent
                        @dragenter.prevent
                      >
                        + Drag product here to add
                      </div>
                      <div v-if="n < Math.max(1, 3 - getEveningProductsForPage(pageIndex - 1).length)" class="arrow-down"><img src="../../../../images/pdf-down-arrow.png" alt="Down arrow"></div>
                    </div>
                  </template>

                  <!-- Additional info removed -->
                </div>

              </div><!-- /regimen-columns -->
            </div>

            
          </div>
        </div>
      </div>
      
      <!-- Sticky Footer -->
      <div class="sticky-footer">
        <div class="footer-left">
          <div class="footer-info">
            <p class="footer-title">{{ regimen.name || 'Preset Regimen' }}</p>
            <span class="footer-subtitle">{{ totalProductsCount }} Products</span>
          </div>
          
          <div v-if="totalPrice > 0" class="footer-price">
            <span class="price-label">Total Price:</span>
            <strong class="price-value">${{ totalPrice.toFixed(2) }}</strong>
          </div>
        </div>
        
        <div class="footer-actions">
          <button
            type="button"
            class="btn_custom btn_line"
            @click="leaveWithoutSaving"
          >
            Back
          </button>
          <button
            type="button"
            class="btn_custom btn_prime"
            :disabled="loading || totalProductsCount === 0"
            @click="completeRegimen"
          >
            {{ loading ? 'Saving...' : 'Complete Preset Regimen' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template> 

 <style>

 /* ── HEADER LOGOS ──────────────────────────────── */
      .patient-change-regimen-new .header-logos {
        display: inline-flex;
        align-items: center;
        gap: 0;
      }
      .patient-change-regimen-new .header-logos img.has-practice-logo {
        padding-right: 8px;
        border-right: 1px solid #E0E0E0;
        margin-right: 8px;
      }
      .patient-change-regimen-new .header-logos img.practice-logo {
        height: 36px;
        max-width: 100px;
        object-fit: contain;
      }

 body.regimen-editor-active{
        overflow-y: hidden;
      }
      .partition {
        display: flex;
        min-height: 100%;
      }

      .partition-left {
        width: 50%;
        flex-shrink: 0;
        border-right: 1px solid #E4E4E4;
        background: #F7F7F7;
        padding: 2rem 0;
        position: relative;
        overflow-y: scroll;
        height: calc(100vh - 200px);
      }
      .section-top{
        padding: 0 24px;
      }

      .partition-right {
        flex: 1;
        padding: 32px 24px 0;
        overflow-y: auto;
        height: calc(100vh - 200px);
      }

      /* ── PANEL TITLE ─────────────────────────────── */
      .panel-title {
        font-size: 25px;
        font-weight: 800;
        color: #1a1a2e;
        margin-bottom: 16px;
      }

      /* ── SEARCH BAR ──────────────────────────────── */
      .search-bar {
        display: flex;
        align-items: center;
        gap: .6rem;
        border: 1px solid #E4E4E4;
        border-radius: 12px;
        padding: 0 0 0 1rem;
        background: #fff;
        margin-bottom: 24px;
        transition: border-color .18s;
      }
      .search-bar:focus-within {
        border-color: #9F7ED9;
      }
      .search-bar input[type="text"] {
        border: none;
        outline: none;
        flex: 1;
        font-size: 16px;
        color: #555;
        background: transparent;
        padding: 10px 20px 10px 0;
      }
      .search-bar input[type="text"]:focus { outline: none; box-shadow: none; border: none; }
      .search-bar input[type="text"]::placeholder { color: #bbb; }

      /* ── FILTERS ─────────────────────────────────── */
      .filters-container {
        margin-bottom: 0;
        border-bottom: 1px solid #E4E4E4;
      }

      .filters-title {
        font-size: 16px;
        font-weight: 700;
        color: #1a1a2e;
        margin-bottom: .75rem;
      }

      .filters-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: .5rem .6rem;
        margin-bottom: 16px;
      }

      .filter-dropdown {
        width: 100%;
        border-radius: 12px;
      }
      .filter-dropdown select,
      .filter-dropdown .select-trigger,
      .filter-dropdown .vs__dropdown-toggle {
        width: 100%;
        border: 1px solid var(--border) !important;
        border-radius: var(--radius) !important;
        padding: .5rem .85rem !important;
        font-size: .88rem !important;
        color: #444 !important;
        background-color: #fff !important;
        cursor: pointer;
        appearance: none;
        -webkit-appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 16 16'%3E%3Cpath fill='%23888' d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E") !important;
        background-repeat: no-repeat !important;
        background-position: right .8rem center !important;
        box-shadow: none !important;
        transition: border-color .18s;
      }
      .filter-dropdown select:focus,
      .filter-dropdown .vs__dropdown-toggle:focus-within {
        border-color: var(--accent) !important;
        outline: none;
      }

      .patient-change-regimen-new .form-filter {
        background-color: white;
        border: solid 1px #dbdadb;
        border-radius: 12px;
        padding-left: 1em;
        padding-top: 0.1em;
        margin-bottom: 15px;
      }
      .patient-change-regimen-new .form-filter .form1-items{
        margin: 1em 0;
      }

      /* ── SEARCH STATUS & ACTIVE FILTERS ─────────── */
      .search-status-section {
        padding: 12px 24px;
        background: #F7F7F7;
        border-bottom: 1px solid #E4E4E4;
        display: flex;
        flex-direction: column;
        gap: 8px;
      }

      .search-indicator {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        font-weight: 500;
        padding: 6px 12px;
        border-radius: 6px;
        width: fit-content;
      }

      .search-indicator.searching {
        color: #6366f1;
        background: #eef2ff;
      }

      .search-indicator.completed {
        color: #059669;
        background: #d1fae5;
      }

      .search-indicator svg {
        flex-shrink: 0;
      }

      .spinner {
        animation: spin 1s linear infinite;
      }

      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }

      .active-filters {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .filter-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        align-items: center;
      }

      .filter-tag {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 8px 4px 10px;
        background: #fff;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 13px;
        color: #374151;
        cursor: pointer;
        transition: all 0.15s;
        font-weight: 500;
      }

      .filter-tag:hover {
        background: #fee2e2;
        border-color: #fca5a5;
        color: #dc2626;
      }

      .filter-tag:hover .filter-tag-close {
        color: #dc2626;
      }

      .filter-tag-label {
        line-height: 1;
      }

      .filter-tag-close {
        color: #6b7280;
        transition: color 0.15s;
        flex-shrink: 0;
      }

      .clear-all-filters {
        padding: 4px 10px;
        background: transparent;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 13px;
        color: #6b7280;
        cursor: pointer;
        transition: all 0.15s;
        font-weight: 500;
        line-height: 1;
      }

      .clear-all-filters:hover {
        background: #f3f4f6;
        border-color: #9ca3af;
        color: #374151;
      }

      /* ── DRAGGABLE ──────────────────────────────── */
      .product-wrapper {
        cursor: move;
        cursor: grab;
      }
      
      .product-wrapper:active {
        cursor: grabbing;
        opacity: 0.7;
      }
      
      .draggable-container {
        min-height: 10px;
      }
      
      .drop-zone-wrapper {
        margin-bottom: 0;
      }
      
      .ghost {
        opacity: 0.5;
        background: #f5f0fd;
      }
      
      .dragging-to-remove .drop-zone {
        background: #f0f0f0 !important;
        border-color: #9ca3af !important;
        opacity: 0.6;
        transition: background 0.2s ease, border-color 0.2s ease, opacity 0.2s ease;
      }

      /* ── PRODUCTS SECTION ────────────────────────── */
      .products-section { 
        margin-top: 10px; 
        padding: 0 10px;
      }

      .section-title {
        font-size: 16px;
        font-weight: 700;
        color: #1a1a2e;
        margin-bottom: 1rem;
        padding: 0 17px;
      }

      .loading-state,
      .no-products {
        text-align: center;
        color: #222222;
        padding: 2rem 0;
        font-size: 16px;
      }

      /* ── PRODUCT LIST ────────────────────────────── */
      .products-list {
        padding: 0 16px 0 17px;
      }

      .product-wrapper {
        border: 1px solid #EBEBEB;
        margin: 0 0 5px 0;
        border-radius: 12px;
        background: #fff;
        overflow: hidden;
        transition: box-shadow .18s, border-color .18s;
      }
      .product-wrapper:hover {
        border-color: #9F7ED9;
        box-shadow: 0 4px 18px rgba(159, 126, 217, .13);
      }

      .product-item {
        display: flex;
        align-items: center;
        gap: .75rem;
        padding: 10px 20px;
        cursor: grab;
      }
      .product-item:active { cursor: grabbing; }

      .product-info-wrapper {
        display: flex;
        align-items: center;
        gap: .85rem;
        flex: 1;
        min-width: 0;
      }

      .patient-change-regimen-new .product-image {
        width: 40px;
        height: 40px;
        border: 1px solid #F2F2F2;
        object-fit: contain;
        flex-shrink: 0;
        border-radius: 6px;
        background: #f3f3f9;
      }

      .patient-change-regimen-new .product-details {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }

      .product-name {
        font-size: 16px;
        font-weight: 400;
        color: #6b7280;
        margin: 0 0 3px;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .product-vendor {
        font-size: 16px;
        font-weight: 700;
        color: #111827;
        margin: 0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .product-type  { display: none; }
      .product-price { display: none; }

      .patient-change-regimen-new .product-actions {
        display: flex;
        align-items: center;
        gap: 32px;
        flex-shrink: 0;
      }

      .patient-change-regimen-new .product-actions a.btn-view {
        font-size: 14px;
        font-weight: 600;
        color: #111827;
        text-decoration: underline;
        cursor: pointer;
        white-space: nowrap;
        transition: color .15s;
      }
      .btn-view:hover { color: #9F7ED9; }

      .btn_prime {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 34px;
        height: 34px;
        background: #6c63b6;
        border: none;
        border-radius: 8px;
        cursor: pointer;
        transition: background .18s, opacity .18s;
        flex-shrink: 0;
      }
      .btn_prime:hover { background: #5a52a3; }
      .btn_prime:disabled {
        background: #e5e7eb;
        cursor: not-allowed;
        opacity: 0.6;
        color: #9ca3af;
      }
      .btn_prime.btn_added {
        background: #86efac;
        cursor: not-allowed;
        opacity: 1;
      }
      .btn_prime svg { display: block; }

      /* ── TIME BUTTONS ──────────────────────────── */
      .product-item .time-btn {
        background: #F9F9F9;
      }
      
      .time-btn {
        width: 80px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        background: #fff;
        border: 1px solid #E4E4E4;
        border-radius: 8px;
        padding: 4px 12px;
        cursor: pointer;
        color: #374151;
        transition: border-color .15s, color .15s, background .15s;
        line-height: 1;
        white-space: nowrap;
      }
      .time-btn:hover {
        border-color: #9F7ED9;
        color: #9F7ED9;
        background: #f5f0fd;
      }
      .time-btn.disabled,
      .time-btn:disabled {
        opacity: 0.4;
        cursor: not-allowed;
        pointer-events: none;
        background: #f5f5f5;
      }
      .time-btn--both {
        padding: .38rem .55rem;
      }

      .time-actions {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      /* ── EXPANDABLE DESCRIPTION ──────────────────── */
      .product-description {
        border-top: 1px solid var(--border);
        padding: .75rem 1rem;
        background: #fafafa;
      }
      .description-content h5 {
        font-size: 12px;
        font-weight: 700;
        margin-bottom: .4rem;
        color: #333;
      }
      .description-text {
        font-size: .83rem;
        color: #000000;
        line-height: 1.5;
      }
      .no-description {
        font-size: .83rem;
        color: #222222;
        margin: 0;
      }

      /* ── PAGINATION ──────────────────────────────── */
      .pagination-wrap {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: 1rem;
        padding-top: .75rem;
        border-top: 1px solid var(--border);
        flex-wrap: wrap;
        gap: .5rem;
      }

      .page-results {
        display: flex;
        align-items: center;
        gap: .4rem;
        font-size: 12px;
        color: #000000;
      }
      .page-results select {
        border: 1px solid var(--border);
        border-radius: 6px;
        padding: .2rem .4rem;
        font-size: 12px;
        cursor: pointer;
        color: #444;
      }
      .page-results select:focus { outline: none; border-color: var(--accent); }

      .pagination-r .pagination {
        display: flex;
        list-style: none;
        margin: 0;
        padding: 0;
        gap: .3rem;
      }
      .pagination-r .pagination li a {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 32px;
        height: 32px;
        border: 1px solid var(--border);
        border-radius: 7px;
        color: #000000;
        text-decoration: none;
        font-size: 16px;
        transition: border-color .15s, color .15s;
      }
      .pagination-r .pagination li a:hover:not(.disabled) {
        border-color: var(--accent);
        color: var(--accent);
      }
      .pagination-r .pagination li a.disabled {
        opacity: .4;
        cursor: not-allowed;
        pointer-events: none;
      }

      /* ── SCROLLBARS ──────────────────────────────── */
      .partition-left::-webkit-scrollbar,
      .partition-right::-webkit-scrollbar     { width: 10px; }

      .partition-left::-webkit-scrollbar-track,
      .partition-right::-webkit-scrollbar-track { background: #FFFFFF; }

      .partition-left::-webkit-scrollbar-thumb,
      .partition-right::-webkit-scrollbar-thumb {
        background: #9F7ED9;
        border-radius: 8px;
        border: 2px dashed transparent;
        background-clip: padding-box;
      }

      /* ═══════════════════════════════════════════════
        RIGHT PANEL — REGIMEN
      ═══════════════════════════════════════════════ */

      .regimen-header { margin-bottom: 1.25rem; }

      .regimen-title {
        font-size: 25px;
        font-weight: 700;
        color: #000000;
        margin-bottom: 16px;
      }

      .regimen-subtitle {
        font-family: 'manrope', sans-serif;
        font-size: 14px;
        font-weight: 400;
        color: #222222;
        margin: 0;
      }

      .regimen-preview-block {
        display: flex;
        width: 100%;
        border: 1px solid #F4F4F4;
        flex-direction: column;
        margin-bottom: 24px;
      }

      .regimen-brand {
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-bottom: 1px solid #F4F4F4;
      }
      .client-details{
        font-size: 12px;
      }

      .brand-avatar {
        width: 30px;
        height: 30px;
        background: var(--accent);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        font-weight: 700;
        font-size: .72rem;
        flex-shrink: 0;
      }

      .brand-name {
        width: 75px;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .brand-name img:first-child {
        padding-right: 5px;
        border-right:1px solid #E0E0E0;
      }
      .brand-name img.no-border:first-child {
        border-right: none !important;
      }
      .brand-name img:last-child {
        padding-left: 5px;
      }


      /* ── REGIMEN COLUMNS ─────────────────────────── */
      .regimen-columns {
        display: grid;
        grid-template-columns: 2fr 2fr;
        height: 100%;
      }

      .regimen-col-title {
        position: relative;
        text-align: center;
        font-weight: 500;
        font-size: 14px;
        line-height: 1;
        padding-bottom: 6px;
        margin-bottom: 17px;
        color: #1a1a2e;
      }
      .regimen-col-title:after {
        content: "";
        background: #9F7ED9;
        width: 44px;
        height: 2px;
        position: absolute;
        left: 50%;
        top: 18px;
        transform: translate(-50%, 50%);
      }

      .regimen-col-evening,
      .regimen-col-morning {
        padding: 16px 13px;
      }

      /* Product cards with numbers */
      .with-product {
        position: relative;
        padding-left: 28px; /* room for the number */
      }

      /* Number renders inside top-left of the card */
      .product-number {
        position: absolute;
        left: -6px;
        top: -6px;
        font-size: 8px;
        font-weight: 700;
        color: #000000;
        line-height: 1;
        width: 17px;
        height: 17px;
        background: #ffffff;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
        border: 1px solid #F2F2F2;
      }

      .regimen-col-evening { background: #F1ECF9; }

      .drop-zone {
        border: 1px solid #E0E0E0;
        border-radius: 8px;
        min-height: 63px;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 20px;
        font-weight: 700;
        font-size: 12px;
        line-height: 1;
        text-align: center;
        color: #000000;
        background: #fff;
        cursor: pointer;
        padding: 8px 12px;
        transition: border-color .18s, background .18s, color .18s;
        box-shadow: 0px 4px 4px 0px #9F7ED952;
      }
      .drop-zone:hover { border: 1px dashed #DFC5FE; }
      .product-name{
        gap: 4px;
      }
      .drop-zone.with-product{
        position: relative;
      }
      .drop-zone.with-product img {
        width: 47px;
        height: 47px;
        border: 1px solid #9F7ED9;
        border-radius: 12px;
        object-fit: contain;
        background: #ffffff;
      }
      .drop-zone .product-name p { 
        font-weight: 500;
        font-size: 11px;
        line-height: 1;
        margin: 0;
        color: #000000;
       }
      .drop-zone .product-name span{
        font-weight: 700;
        font-size: 9px;
        line-height: 1;
        color: #000000;
       }
      .drop-zone .product-name a{
        font-weight: 700;
        font-size: 10px;
        line-height: normal;
        text-decoration: underline;
        color: #000000;
        margin-top: 2px;
        width: fit-content;
       }
      .arrow-down {
        display: flex;
        justify-content: center;
        margin: .35rem 0;
      }
      .arrow-down img {
        width: 21px;
        height: 21px;
      }
      
      /* ── MODAL STYLES ──────────────────────────────── */
      .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
      }
      
      .modal-overlay .modal-content {
        background: white;
        border-radius: 12px;
        width: 90%;
        max-width: 650px;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
        display: flex;
        flex-direction: column;
        padding: 24px;
      }
      
      .modal-overlay .modal-content .modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0px;
        border-bottom: 0px solid;
        padding: 0;
      }
      
      .modal-header h3 {
        font-size: 16px;
        font-weight: 400;
        color: #111827;
        margin: 0;
      }
      
      .close-btn {
        background: none;
        border: none;
        font-size: 28px;
        color: #6B7280;
        cursor: pointer;
        padding: 0;
        width: 32px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        line-height: 1;
      }
      .close-btn:hover { color: #111827; }
      
      .modal-content .modal-body {
        padding: 12px 0;
      }
      
      .modal-content .modal-footer {
        padding: 0;
        border-top: 0px solid;
        display: flex;
        justify-content: flex-end;
        gap: 12px;
      }

      .modal-content .modal-footer .btn_custom.btn_prime{
        width: 273px;
        height: 44px;
        min-width: 273px;
      }
      .modal-content .modal-footer .btn_custom.btn_line{
        height: 44px;
        color: #2C3146;
        font-size: 14px;
        font-weight: 400;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .footer-actions .btn_custom.btn_prime{
        width: 273px;
        height: auto;
        min-width: 273px;
      }
      .footer-actions .btn_custom.btn_prime:disabled {
        background: #e5e7eb;
        cursor: not-allowed;
        opacity: 0.6;
        color: #9ca3af;
      }

      /* ── RESPONSIVE ──────────────────────────────── */
      @media (max-width: 991px) {
        .partition { flex-direction: column; }
        .partition-left {
          width: 100%;
          max-width: 100%;
          border-right: none;
          border-bottom: 1px solid var(--border);
        }
        .partition-right { padding: 1.5rem 1rem; }
      }

      @media (max-width: 767px) {
        .filters-grid { grid-template-columns: 1fr; }
        .product-price { display: none; }
        .product-type  { display: none; }
      }

      @media (max-width: 575px) {
        .regimen-columns { grid-template-columns: 1fr; }
        .partition-left,
        .partition-right { padding: 1rem; }
        .panel-title   { font-size: 25px; }
        .regimen-title { font-size: 1.4rem; }
        .product-item  { gap: .5rem; padding: .6rem .75rem; }
      }
 </style>

<script>
import logo from '@images/logo-light.png';
import draggable from 'vuedraggable';
import productList from '@/mixins/productList';
import FilterSelect from '@/views/dashboard/partials/FilterSelect';
import TopPicksNew from '@/views/dashboard/partials/TopPicksNew';
import { PresetRegimenService, RegimenService } from '@/services/api.service';

// @group Preset Regimen
// Component to create/edit preset regimen with products
export default {
  name: 'RegimenCreatePresetNew',
  components: {
    draggable,
    FilterSelect,
    TopPicksNew
  },
  mixins: [productList],
  props: {
    // @vuese
    // Regimen object
    regimen: {
      type: Object,
      default() {
        return {};
      }
    }
  },
  data() {
    return {
      regimenId: null,
      logo,
      loading: false,
      expandedProduct: null,
      morningProducts: [],
      eveningProducts: [],
      productsType: '',
      dragOptions: {
        animation: 200,
        group: 'products',
        disabled: false,
        ghostClass: 'ghost'
      },
      isDraggingOutside: false,
      draggedProduct: null,
      draggedSection: null,
      duplicateRejectedProduct: null,
      isSearching: false,
      searchCompleted: false,
      searchCompletedTimeout: null,
      physicianName: '',
      physicianId: null
    };
  },
  computed: {
    // Practice logo to show alongside RegimenPro logo
    practiceLogo() {
      if (this.regimen && this.regimen.practice && this.regimen.practice.logo && this.regimen.practice.logo.url) {
        return this.regimen.practice.logo.url;
      }
      const storePractice = this.$store && this.$store.state && this.$store.state.practice && this.$store.state.practice.practice;
      if (storePractice && storePractice.logo && storePractice.logo.url) {
        return storePractice.logo.url;
      }
      return null;
    },
    // Get active filters for display
    activeFilters() {
      const filters = [];
      if (this.brand) filters.push({ type: 'brand', label: this.brand, value: this.brand });
      if (this.concern) filters.push({ type: 'concern', label: this.concern, value: this.concern });
      if (this.category) filters.push({ type: 'category', label: this.category, value: this.category });
      if (this.ingredient) filters.push({ type: 'ingredient', label: this.ingredient, value: this.ingredient });
      if (this.search) filters.push({ type: 'search', label: `Search: "${this.search}"`, value: this.search });
      return filters;
    },
    totalProductsCount() {
      return this.morningProducts.length + this.eveningProducts.length;
    },
    totalPrice() {
      // Use a Map to track unique products and count each only once
      const uniqueProducts = new Map();
      
      // Add all products from all sections
      [...this.morningProducts, ...this.eveningProducts].forEach(product => {
        // Use variant_graph_id or shopify_product_graph_id as unique identifier
        const key = product.variant_graph_id || product.shopify_product_graph_id || product.id;
        if (key && !uniqueProducts.has(key)) {
          uniqueProducts.set(key, parseFloat(product.price || 0));
        }
      });
      
      // Sum up prices of unique products
      let total = 0;
      uniqueProducts.forEach(price => {
        total += price;
      });
      
      return total;
    },
    // Calculate total number of pages needed
    totalPages() {
      const morningPages = Math.ceil(this.morningProducts.length / 8) || 1;
      const eveningPages = Math.ceil(this.eveningProducts.length / 8) || 1;
      return Math.max(morningPages, eveningPages);
    },
    // Calculate morning pages
    morningPages() {
      return Math.ceil(this.morningProducts.length / 8) || 1;
    },
    // Calculate evening pages
    eveningPages() {
      return Math.ceil(this.eveningProducts.length / 8) || 1;
    },
    // Get morning products for a specific page
    getMorningProductsForPage() {
      return (pageIndex) => {
        const start = pageIndex * 8;
        const end = start + 8;
        return this.morningProducts.slice(start, end);
      };
    },
    // Get evening products for a specific page
    getEveningProductsForPage() {
      return (pageIndex) => {
        const start = pageIndex * 8;
        const end = start + 8;
        return this.eveningProducts.slice(start, end);
      };
    },
    // Check if this is the last page
    isLastPage() {
      return (pageIndex) => pageIndex === this.totalPages - 1;
    },
    // Check if this is the last morning page
    isLastMorningPage() {
      return (pageIndex) => pageIndex === this.morningPages - 1;
    },
    // Check if this is the last evening page
    isLastEveningPage() {
      return (pageIndex) => pageIndex === this.eveningPages - 1;
    },
    // Check if a product is in morning regimen
    isProductInMorning() {
      return (product) => {
        return this.morningProducts.some(p => p.shopify_product_graph_id === product.id);
      };
    },
    // Check if a product is in evening regimen
    isProductInEvening() {
      return (product) => {
        return this.eveningProducts.some(p => p.shopify_product_graph_id === product.id);
      };
    },
    // Check if product should be hidden from list
    shouldHideProduct() {
      return (product) => {
        const inMorning = this.isProductInMorning(product);
        const inEvening = this.isProductInEvening(product);
        
        // Hide if in both morning and evening
        return inMorning && inEvening;
      };
    },
    // Filtered products for display
    filteredProducts() {
      if (!this.products || !Array.isArray(this.products)) {
        return [];
      }
      return this.products.filter(product => {
        // Filter out Gift Cards
        if (product.productType === 'Gift Cards') {
          return false;
        }
        // Filter out products already added
        return !this.shouldHideProduct(product);
      });
    }
  },
  watch: {
      // Automatically fetch next page when all products are added
      filteredProducts(newVal) {
        // If all products are added (filteredProducts is empty), more products exist, and not currently loading
        if (Array.isArray(newVal) && newVal.length === 0 && this.hasNextPage && !this.sending) {
          this.getMoreProducts();
        }
      },
    regimen: {
      handler: 'onRegimenChange',
      immediate: true,
      deep: true
    },
    // Watch route params to reload data when navigating to edit page
    '$route.params.id': {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.regimenId = newId;
          this.loadRegimenProducts();
        }
      },
      immediate: true
    }
  },
  mounted() {
    // Add body class to prevent scrolling
    document.body.classList.add('regimen-editor-active');
    
    // Check if we're editing an existing regimen from route params
    const regimenId = this.$route.params.id;
    if (regimenId && !this.regimenId) {
      this.regimenId = regimenId;
    }
    
    // Initialize filters
    const practiceId = (this.regimen && this.regimen.practice && this.regimen.practice.id) || (this.practice && this.practice.id);
    if (practiceId) {
      this.getFilters({practice: practiceId});
    } else {
      this.getFilters();
    }
  },
  beforeDestroy() {
    // Remove body class when leaving component
    document.body.classList.remove('regimen-editor-active');
    
    // Clean up timeouts
    if (this.searchCompletedTimeout) {
      clearTimeout(this.searchCompletedTimeout);
    }
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
  methods: {
    // @vuese
    // Handle regimen prop changes (called by watcher)
    async onRegimenChange() {
      // Load regimen data and wait for it to complete
      await this.fillData();
      
      if (this.productsType !== 'topPicks' || this.getTopPicksPhysicianId()) {
        this.fetchData();
      }
    },
    
    // @vuese
    // Used to redirect back to regimen info page
    leaveWithoutSaving() {
      if (this.regimen.id) {
        this.$router.push({name: 'RegimenInfoNewEdit', params: {id: this.regimen.id}});
      } else {
        this.$router.push({name: 'RegimenInfoNew'});
      }
    },
    
    // @vuese
    // Used to fill regimen data in local store
    async fillData() {
      // Check if regimen prop exists and has required properties
      if (!this.regimen || typeof this.regimen !== 'object') {
        return;
      }
      
      // Extract physician data from regimen prop if available
      // Handle both user object and direct ID
      let physicianIdFromProp = null;
      
      if (this.regimen.user && typeof this.regimen.user === 'object') {
        // API returns user object
        const firstName = this.regimen.user.first_name || '';
        const lastName = this.regimen.user.last_name || '';
        this.physicianName = (firstName + ' ' + lastName).trim();
        physicianIdFromProp = this.regimen.user.id;
      } else if (this.regimen.physician && typeof this.regimen.physician === 'object') {
        // API returns physician object
        const firstName = this.regimen.physician.first_name || '';
        const lastName = this.regimen.physician.last_name || '';
        this.physicianName = (firstName + ' ' + lastName).trim();
        physicianIdFromProp = this.regimen.physician.id;
      } else {
        // Check both physician_id and user_id fields (direct IDs)
        physicianIdFromProp = this.regimen.physician_id || this.regimen.user_id;
      }
      
      if (physicianIdFromProp) {
        this.physicianId = physicianIdFromProp;
      }
      
      // Get regimen ID from route params or regimen prop
      const regimenId = this.$route.params.id || (this.regimen && this.regimen.id);

      if (regimenId) {
        this.regimenId = regimenId;
        // Load existing regimen products after setting regimenId
        await this.loadRegimenProducts();
      }
    },
    
    // @vuese
    // Load existing regimen products from API
    async loadRegimenProducts() {
      if (!this.regimenId) {
        console.log('loadRegimenProducts: No regimen ID');
        return;
      }
      
      console.log('Loading regimen products for regimen ID:', this.regimenId);
      
      try {
        const practiceParam = this.getRequestPracticeId();
        console.log('Practice param:', practiceParam);
        const response = await RegimenService.getRegimenProducts(this.regimenId, practiceParam);
        const data = response.data;
        
        console.log('Regimen products loaded:', data);
        
        // Helper function to transform prescription badge products
        const transformProduct = (product) => {
          if (product.item_type === 'prescription_badge') {
            // Transform prescription badge to Rx format
            return {
              ...product,
              handle: 'rx',
              name: 'Rx',
              title: 'Rx',
              brand: '',
              vendor: '',
              image: require('@images/rx.png'),
              price: 0,
              // Keep the shopify_product_graph_id from API for identification
              shopify_product_graph_id: product.shopify_product_graph_id || `rx-${product.id}`,
              variant_graph_id: product.variant_graph_id || `rx-variant-${product.id}`
            };
          }
          return product;
        };
        
        // Load morning products
        if (data.morning && data.morning.products) {
          this.morningProducts = data.morning.products.map(transformProduct);
        }
        
        // Load evening products
        if (data.evening && data.evening.products) {
          this.eveningProducts = data.evening.products.map(transformProduct);
        }
      } catch (error) {
        console.error('Failed to load regimen products:', error);
        // If products don't exist, keep arrays empty (already initialized as [])
      }
    },
    
    // @vuese
    // Start dragging a product
    startDragProduct(event, product) {
      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.setData('product', JSON.stringify(product));
      event.dataTransfer.setData('type', 'product');
      
      // Create custom drag image
      const dragImage = document.createElement('div');
      dragImage.style.cssText = `
        position: absolute;
        top: -1000px;
        left: -1000px;
        background: white;
        border: 2px solid #9F7ED9;
        border-radius: 12px;
        padding: 16px 20px;
        box-shadow: 0 4px 12px rgba(159, 126, 217, 0.3);
        width: 660px;
        min-width: 660px;
        z-index: 10000;
      `;
      dragImage.innerHTML = `
        <div style="font-size: 14px; font-weight: 700; color: #111827; margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${product.title}</div>
        <div style="font-size: 12px; color: #6b7280; white-space: nowrap;">Let go to drop in position</div>
      `;
      document.body.appendChild(dragImage);
      event.dataTransfer.setDragImage(dragImage, 330, 30);
      
      // Remove drag image after drag ends
      setTimeout(() => {
        document.body.removeChild(dragImage);
      }, 0);
    },
    
    // @vuese
    // Handle drop in morning section
    onDropMorning(event) {
      event.preventDefault();
      const type = event.dataTransfer.getData('type');
      
      if (type === 'product') {
        const product = JSON.parse(event.dataTransfer.getData('product'));
        this.addProductToSection(product, 'morning');
      }
    },
    
    // @vuese
    // Handle drop in evening section
    onDropEvening(event) {
      event.preventDefault();
      const type = event.dataTransfer.getData('type');
      
      if (type === 'product') {
        const product = JSON.parse(event.dataTransfer.getData('product'));
        this.addProductToSection(product, 'evening');
      }
    },
    
    // @vuese
    // Add product to specific section
    addProductToSection(product, timeOfUse) {
      const isInMorning = this.morningProducts.some(p => p.shopify_product_graph_id === product.id);
      const isInEvening = this.eveningProducts.some(p => p.shopify_product_graph_id === product.id);
      
      if (timeOfUse === 'morning' && isInMorning) {
        this.flashWarning('Product is already in Morning regimen');
        return;
      }
      
      if (timeOfUse === 'evening' && isInEvening) {
        this.flashWarning('Product is already in Evening regimen');
        return;
      }
      
      // Extract variant data
      const variant = product.variants && product.variants[0] ? product.variants[0] : null;
      
      // Extract numeric IDs from GraphQL ID strings
      const extractNumericId = (graphqlId) => {
        if (!graphqlId) return null;
        const parts = graphqlId.split('/');
        return parts[parts.length - 1];
      };
      
      const productNumericId = extractNumericId(product.id);
      const variantNumericId = variant ? extractNumericId(variant.id) : null;
      
      // Transform Shopify product to regimen product format
      const regimenProduct = {
        id: null,
        shopify_product_id: productNumericId,
        shopify_product_graph_id: product.id,
        variant_id: variantNumericId,
        variant_graph_id: variant ? variant.id : null,
        discount_variant_id: null,
        handle: product.handle || '',
        name: product.title,
        title: product.title,
        variant_title: variant ? variant.title : '',
        brand: product.vendor,
        vendor: product.vendor,
        sku: variant ? variant.sku : '',
        image: (product.images && product.images.edges && product.images.edges[0] && product.images.edges[0].node && product.images.edges[0].node.url) || (product.images && product.images[0] && product.images[0].url) || '/images/product-placeholder.jpg',
        price: variant ? parseFloat(variant.price.amount) : 0,
        frequency: "One Time",
        usage_instruction: '',
        recomendation_type: 'core',
        productType: product.productType || 'Product',
        selling_plan_id: "",
        sample_sku: '',
        full_size_url: '',
        manufacturer_sku: null,
        position: 0
      };
      
      // Add to appropriate section
      if (timeOfUse === 'morning') {
        this.morningProducts.push(regimenProduct);
        this.flashSuccess(`${product.title} added to Morning regimen`);
      } else {
        this.eveningProducts.push(regimenProduct);
        this.flashSuccess(`${product.title} added to Evening regimen`);
      }
    },
    
    // @vuese
    // Add product to regimen based on time (am, pm, or both)
    addProductToRegimen(product, timeOfUse = 'am') {
      // Check if trying to add to morning but already in morning
      if (timeOfUse === 'am' && this.isProductInMorning(product)) {
        this.flashWarning('Product is already in Morning regimen');
        return;
      }
      
      // Check if trying to add to evening but already in evening
      if (timeOfUse === 'pm' && this.isProductInEvening(product)) {
        this.flashWarning('Product is already in Evening regimen');
        return;
      }
      
      // Check if trying to add to both but already in either
      if (timeOfUse === 'both' && (this.isProductInMorning(product) || this.isProductInEvening(product))) {
        this.flashWarning('Product is already in Morning or Evening regimen');
        return;
      }
      
      // Extract variant data
      const variant = product.variants && product.variants[0] ? product.variants[0] : null;
      
      // Extract numeric IDs from GraphQL ID strings
      const extractNumericId = (graphqlId) => {
        if (!graphqlId) return null;
        const parts = graphqlId.split('/');
        return parts[parts.length - 1];
      };
      
      const productNumericId = extractNumericId(product.id);
      const variantNumericId = variant ? extractNumericId(variant.id) : null;
      
      // Transform Shopify product to regimen product format
      const createRegimenProduct = () => ({
        id: null,
        shopify_product_id: productNumericId,
        shopify_product_graph_id: product.id,
        variant_id: variantNumericId,
        variant_graph_id: variant ? variant.id : null,
        discount_variant_id: null,
        handle: product.handle || '',
        name: product.title,
        title: product.title,
        variant_title: variant ? variant.title : '',
        brand: product.vendor,
        vendor: product.vendor,
        sku: variant ? variant.sku : '',
        image: (product.images && product.images.edges && product.images.edges[0] && product.images.edges[0].node && product.images.edges[0].node.url) || (product.images && product.images[0] && product.images[0].url) || '/images/product-placeholder.jpg',
        price: variant ? parseFloat(variant.price.amount) : 0,
        frequency: "One Time",
        usage_instruction: '',
        recomendation_type: 'core',
        productType: product.productType || 'Product',
        selling_plan_id: "",
        sample_sku: '',
        full_size_url: '',
        manufacturer_sku: null,
        position: 0
      });
      
      // Add to appropriate section(s)
      if (timeOfUse === 'am') {
        this.morningProducts.push(createRegimenProduct());
        this.flashSuccess(`${product.title} added to Morning regimen`);
      } else if (timeOfUse === 'pm') {
        this.eveningProducts.push(createRegimenProduct());
        this.flashSuccess(`${product.title} added to Evening regimen`);
      } else if (timeOfUse === 'both') {
        this.morningProducts.push(createRegimenProduct());
        this.eveningProducts.push(createRegimenProduct());
        this.flashSuccess(`${product.title} added to Morning and Evening regimen`);
      }
    },
    
    // @vuese
    // Remove product from regimen
    removeProduct(product, section) {
      let targetArray;
      
      switch(section) {
        case 'morning':
          targetArray = this.morningProducts;
          break;
        case 'evening':
          targetArray = this.eveningProducts;
          break;
      }
      
      const index = targetArray.findIndex(p => p.variant_graph_id === product.variant_graph_id);
      if (index !== -1) {
        targetArray.splice(index, 1);
      }
    },

    // @vuese
    // Get array by regimen section
    getSectionArray(section) {
      switch(section) {
        case 'morning':
          return this.morningProducts;
        case 'evening':
          return this.eveningProducts;
        default:
          return null;
      }
    },

    // @vuese
    // Compare products consistently across drag events
    isSameDraggedProduct(productA, productB) {
      if (!productA || !productB) return false;

      const keyA = productA.variant_graph_id || productA.shopify_product_graph_id;
      const keyB = productB.variant_graph_id || productB.shopify_product_graph_id;
      return keyA && keyA === keyB;
    },

    // @vuese
    // Handle drag changes for paginated sections (morning/evening)
    handlePaginatedDragChange(event, section, pageIndex) {
      const targetArray = this.getSectionArray(section);
      if (!targetArray) return;

      const pageOffset = pageIndex * 8;

      if (event.moved) {
        const globalOldIndex = pageOffset + event.moved.oldIndex;
        const globalNewIndex = pageOffset + event.moved.newIndex;
        const [movedItem] = targetArray.splice(globalOldIndex, 1);
        targetArray.splice(globalNewIndex, 0, movedItem);
      }

      // Reorder inside the same paginated section.
      if (event.added && this.draggedSection === section) {
        const draggedIndex = targetArray.findIndex(product =>
          this.isSameDraggedProduct(product, event.added.element)
        );

        if (draggedIndex !== -1) {
          const [draggedItem] = targetArray.splice(draggedIndex, 1);
          let destinationIndex = pageOffset + event.added.newIndex;

          if (draggedIndex < destinationIndex) {
            destinationIndex -= 1;
          }

          destinationIndex = Math.max(0, Math.min(destinationIndex, targetArray.length));
          targetArray.splice(destinationIndex, 0, draggedItem);
        }
      }

      // Move between sections (or from a newly dropped external source).
      if (event.added && this.draggedSection !== section) {
        targetArray.splice(pageOffset + event.added.newIndex, 0, event.added.element);

        const duplicateCount = targetArray.filter(product =>
          product.shopify_product_graph_id === event.added.element.shopify_product_graph_id
        ).length;

        if (duplicateCount > 1) {
          targetArray.splice(pageOffset + event.added.newIndex, 1);
          const sectionName = section === 'morning' ? 'Morning' : 'Evening';
          this.flashWarning(`Product is already in ${sectionName} regimen`);
          // Mark this product as rejected so we don't remove it from source
          this.duplicateRejectedProduct = event.added.element.shopify_product_graph_id;
          return;
        }
        
        // Remove from source section since we're moving to a different section
        const sourceArray = this.getSectionArray(this.draggedSection);
        if (sourceArray) {
          const sourceIndex = sourceArray.findIndex(p =>
            this.isSameDraggedProduct(p, event.added.element)
          );
          if (sourceIndex !== -1) {
            sourceArray.splice(sourceIndex, 1);
          }
        }
      }

      // event.removed is now only for internal tracking - actual removal is handled above
      // This prevents issues with reordering within the same section across pages

      this.updateProductPositions(section);
    },
    
    // @vuese
    // Handle drag change event and check for duplicates
    handleDragChange(event, section) {
      // Check if an item was added (moved from another section or same section)
      if (event.added) {
        const addedProduct = event.added.element;
        let targetArray;
        
        switch(section) {
          case 'morning':
            targetArray = this.morningProducts;
            break;
          case 'evening':
            targetArray = this.eveningProducts;
            break;
        }
        
        // Check if the product already exists in this section (excluding the one just added)
        const duplicateCount = targetArray.filter(p => 
          p.shopify_product_graph_id === addedProduct.shopify_product_graph_id
        ).length;
        
        if (duplicateCount > 1) {
          // Remove the duplicate that was just added
          const addedIndex = event.added.newIndex;
          targetArray.splice(addedIndex, 1);
          
          // Show warning message
          const sectionName = section === 'morning' ? 'Morning' : 'Evening';
          this.flashWarning(`Product is already in ${sectionName} regimen`);
          // Mark this product as rejected so we don't remove it from source
          this.duplicateRejectedProduct = addedProduct.shopify_product_graph_id;
          return;
        }
      }
      
      // Update positions after successful drag
      this.updateProductPositions(section);
    },
    
    // @vuese
    // Update product positions after drag
    updateProductPositions(section) {
      let targetArray;
      
      switch(section) {
        case 'morning':
          targetArray = this.morningProducts;
          break;
        case 'evening':
          targetArray = this.eveningProducts;
          break;
      }
      
      // Update positions
      targetArray.forEach((product, index) => {
        product.position = index;
      });
    },
    
    // @vuese
    // Handle drag start - track which product is being dragged
    onDragStart(event, section, pageIndex = 0) {
      this.draggedSection = section;
      
      // Get the index from the vuedraggable event
      const oldIndex = event.oldIndex;
      
      let targetArray;
      switch(section) {
        case 'morning':
          targetArray = this.morningProducts;
          break;
        case 'evening':
          targetArray = this.eveningProducts;
          break;
      }
      
      if (targetArray && oldIndex !== undefined && targetArray.length > 0) {
        const isPaginatedSection = section === 'morning' || section === 'evening';
        const globalOldIndex = isPaginatedSection ? (pageIndex * 8) + oldIndex : oldIndex;
        this.draggedProduct = targetArray[globalOldIndex];
      }
    },
    
    // @vuese
    // Handle drag end - check if dropped outside and remove
    onDragEnd(event) {
      if (this.isDraggingOutside && this.draggedProduct && this.draggedSection) {
        this.removeProduct(this.draggedProduct, this.draggedSection);
      }
      
      // Reset state
      this.isDraggingOutside = false;
      this.draggedProduct = null;
      this.draggedSection = null;
      this.duplicateRejectedProduct = null;
    },
    
    // @vuese
    // Handle drag leave partition - mark as outside
    onDragLeavePartition(event) {
      // Check if really leaving (not just moving to child element)
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX;
      const y = event.clientY;
      
      if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
        if (this.draggedProduct) {
          this.isDraggingOutside = true;
        }
      }
    },
    
    // @vuese
    // Handle drag enter partition - mark as inside
    onDragEnterPartition(event) {
      if (this.draggedProduct) {
        this.isDraggingOutside = false;
      }
    },
    
    // @vuese
    // Handle drop in partition - ensure we're not removing
    onDropInPartition(event) {
      this.isDraggingOutside = false;
    },
    
    // @vuese
    // Toggle product description
    toggleProduct(productId) {
      this.expandedProduct = this.expandedProduct === productId ? null : productId;
    },
    
    // @vuese
    // Search products with debounce
    searchProduct() {
      this.isSearching = true;
      this.searchCompleted = false;
      this.query = this.buildFilters();
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.getProducts().then(() => {
          this.isSearching = false;
          // Only show "search complete" if there was an actual text search
          if (this.search) {
            this.searchCompleted = true;
            
            // Clear the completed indicator after 2 seconds
            clearTimeout(this.searchCompletedTimeout);
            this.searchCompletedTimeout = setTimeout(() => {
              this.searchCompleted = false;
            }, 2000);
          }
        });
      }, 500);
    },
    
    // @vuese
    // Remove a specific filter
    removeFilter(filterType) {
      switch(filterType) {
        case 'brand':
          this.brand = '';
          break;
        case 'concern':
          this.concern = '';
          break;
        case 'category':
          this.category = '';
          break;
        case 'ingredient':
          this.ingredient = '';
          break;
        case 'search':
          this.search = '';
          break;
      }
      // Use nextTick to ensure the filter components update before triggering search
      this.$nextTick(() => {
        // Use fetchData instead of searchProduct to avoid showing "search complete"
        this.fetchData();
      });
    },
    
    // @vuese
    // Clear all active filters
    clearAllFilters() {
      this.brand = '';
      this.concern = '';
      this.category = '';
      this.ingredient = '';
      this.search = '';
      // Use nextTick to ensure the filter components update before triggering search
      this.$nextTick(() => {
        // Use fetchData instead of searchProduct to avoid showing "search complete"
        this.fetchData();
      });
    },
    
    // @vuese
    // Override fetchData to handle search state
    fetchData() {
      this.isSearching = true;
      this.searchCompleted = false;
      this.query = this.buildFilters();
      this.getProducts().then(() => {
        this.isSearching = false;
      }).catch(() => {
        this.isSearching = false;
      });
    },
    
    // @vuese
    // Change products per page
    changePerPage() {
      this.isSearching = true;
      this.searchCompleted = false;
      this.getProducts().then(() => {
        this.isSearching = false;
      }).catch(() => {
        this.isSearching = false;
      });
    },
    
    // @vuese
    // Complete regimen and save
    async completeRegimen() {
      if (this.loading) return;
      
      // Build products array with am_position/pm_position
      // Use a map to track products and merge if they're in both AM and PM
      const productMap = new Map();
      
      // Add morning products
      this.morningProducts.forEach((p, index) => {
        const productData = {
          id: p.id || null,
          variant_id: p.variant_id,
          shopify_product_id: p.shopify_product_id,
          title: p.title,
          vendor: p.vendor,
          price: String(p.price || '0'),
          am_position: index + 1,
          pm_position: null,
          shopify_product_graph_id: p.shopify_product_graph_id,
          variant_graph_id: p.variant_graph_id,
          discount_variant_id: p.discount_variant_id,
          handle: p.handle,
          variant_title: p.variant_title,
          sku: p.sku,
          image: p.image,
          usage_instruction: p.usage_instruction || '',
          recomendation_type: p.recomendation_type || 'core',
          time_of_use: 'AM',
          frequency: "One Time",
          selling_plan_id: ""
        };
        
        productMap.set(p.shopify_product_graph_id, productData);
      });
      
      // Add evening products (merge if already in morning)
      this.eveningProducts.forEach((p, index) => {
        const existingProduct = productMap.get(p.shopify_product_graph_id);
        
        if (existingProduct) {
          // Product is in both AM and PM - merge them
          existingProduct.pm_position = index + 1;
          existingProduct.time_of_use = 'AM/PM';
        } else {
          // Product is only in PM
          const productData = {
            id: p.id || null,
            variant_id: p.variant_id,
            shopify_product_id: p.shopify_product_id,
            title: p.title,
            vendor: p.vendor,
            price: String(p.price || '0'),
            am_position: null,
            pm_position: index + 1,
            shopify_product_graph_id: p.shopify_product_graph_id,
            variant_graph_id: p.variant_graph_id,
            discount_variant_id: p.discount_variant_id,
            handle: p.handle,
            variant_title: p.variant_title,
            sku: p.sku,
            image: p.image,
            usage_instruction: p.usage_instruction || '',
            recomendation_type: p.recomendation_type || 'core',
            time_of_use: 'PM',
            frequency: "One Time",
            selling_plan_id: ""
          };
          
          productMap.set(p.shopify_product_graph_id, productData);
        }
      });
      
      // Convert map to array
      const products = Array.from(productMap.values());
      
      if (products.length === 0) {
        return;
      }
      
      this.loading = true;
      
      try {
        // Use getRequestPracticeId() which correctly checks for super admin role
        const practiceParam = this.getRequestPracticeId();
        
        // Call bulk_upsert API with new payload format
        const payload = {
          products: products
        };
        
        await PresetRegimenService.bulkUpsertPresetRegimenProducts(
          payload,
          this.regimenId,
          practiceParam
        );

        if (sessionStorage.getItem('presetRegimenCreated') === String(this.regimenId)) {
          this.flashSuccess('Preset regimen created successfully!');
          sessionStorage.removeItem('presetRegimenCreated');
        }
        
        // Open PDF download in new window
        const pdfUrl = practiceParam 
          ? `/api/su/practices${practiceParam}/regimens/${this.regimenId}/download_pdf`
          : `/api/practice/regimens/${this.regimenId}/download_pdf`;
        window.open(pdfUrl, '_blank');
        
        // Redirect to preset regimen list
        this.$router.push({name: 'PresetRegimenNew'});
      }
      catch(error)
      {
        console.error('Failed to update preset regimen:', error);
        this.flashWarning('Failed to update preset regimen. Please try again.');
      }
      finally
      {
        this.loading = false;
      }
    },
    
    // @vuese
    // Get practice ID for super admin user
    getRequestPracticeId() {
      return this.isRole('super_admin') && this.regimen && this.regimen.owner_id ? `/${this.regimen.owner_id}` : '';
    },

    // @vuese
    // Used by productList mixin to load top picks for the preset regimen's physician
    getTopPicksPhysicianId() {
      return this.physicianId;
    },

    handlePresetProduct({ product, section }) {
      this.addProductToRegimen(product, section);
    },
  }
};
</script>
